/**
 * useSkuExamples — discovers per-SKU example images in R2 by probing indices
 * sequentially until one fails to load. Stops at first miss (or hard cap).
 *
 * Returns URLs in order found. Probe uses the browser image cache, so once
 * discovered the URLs render instantly inside the carousel.
 */
import { useEffect, useRef, useState } from 'react';
import { exampleUrl, getExamplesFolderForSku } from '../lib/sku-examples';

const HARD_CAP = 30;

function probeImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

export interface UseSkuExamplesResult {
  urls: string[];
  loading: boolean;
  folder: string | null;
}

export function useSkuExamples(skuCode: string): UseSkuExamplesResult {
  const folder = getExamplesFolderForSku(skuCode);
  const [urls, setUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(folder !== null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!folder) {
      setUrls([]);
      setLoading(false);
      return;
    }
    setUrls([]);
    setLoading(true);

    (async () => {
      const found: string[] = [];
      for (let i = 1; i <= HARD_CAP; i += 1) {
        if (cancelledRef.current) return;
        const url = exampleUrl(folder, i);
        const ok = await probeImage(url);
        if (cancelledRef.current) return;
        if (!ok) break;
        found.push(url);
        setUrls([...found]);
      }
      if (!cancelledRef.current) setLoading(false);
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [folder]);

  return { urls, loading, folder };
}
